import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import SpamFolderReminderModal from './SpamFolderReminderModal';
import PasswordResetModal from './PasswordResetModal';
import axios from 'axios';
import { BACKEND_URL, API_BASE } from '../config/api';

const API = API_BASE;

const AuthModal = ({ children, onSuccess, defaultTab = 'login' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [isVerifying, setIsVerifying] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showUsernameRecovery, setShowUsernameRecovery] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  
  // Password visibility states
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { login, register, verifyEmail, isLoading } = useAuth();

  // Login form state
  const [loginData, setLoginData] = useState({
    username: '',
    password: ''
  });

  // Auto-populate credentials if "remember me" was checked and reset tab when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset to default tab when opening
      setActiveTab(defaultTab);
      
      const rememberMe = localStorage.getItem('rememberMe');
      const lastUsername = localStorage.getItem('lastUsername');
      const lastPassword = localStorage.getItem('lastPassword'); // Basic storage for demo
      
      if (rememberMe === 'true' && lastUsername) {
        setLoginData(prev => ({
          ...prev,
          username: lastUsername,
          password: lastPassword || '' // Auto-fill password if stored
        }));
      }
    }
  }, [isOpen, defaultTab]);

  // Register form state
  const [registerData, setRegisterData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    newsletter_subscribed: false
  });

  // Verification state
  const [verificationCode, setVerificationCode] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.username || !loginData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    const result = await login(loginData.username, loginData.password);
    if (result.success) {
      // Store password temporarily for "remember me" functionality
      localStorage.setItem('tempPassword', loginData.password);
      
      setIsOpen(false);
      setLoginData({ username: '', password: '' });
      if (onSuccess) onSuccess();
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerData.username || !registerData.email || !registerData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (registerData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    const result = await register({
      username: registerData.username,
      email: registerData.email,
      password: registerData.password,
      newsletter_subscribed: registerData.newsletter_subscribed
    });

    if (result.success) {
      setPendingUserId(result.user_id);
      setIsVerifying(true);
      setRegisterData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        newsletter_subscribed: false
      });
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit verification code');
      return;
    }

    const result = await verifyEmail(pendingUserId, verificationCode);
    if (result.success) {
      setIsVerifying(false);
      setPendingUserId(null);
      setVerificationCode('');
      setActiveTab('login');
      toast.success('Account verified! You can now login.');
    }
  };

  const resetModal = () => {
    setLoginData({ username: '', password: '' });
    setRegisterData({
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      newsletter_subscribed: false
    });
    setVerificationCode('');
    setIsVerifying(false);
    setPendingUserId(null);
    setActiveTab('login');
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => {
      setIsOpen(open);
      if (!open) resetModal();
    }}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md relative">
        {/* Standardized Close Button */}
        <button
          onClick={() => setIsOpen(false)}
          className="absolute top-3 right-3 z-50 w-7 h-7 rounded-full flex items-center justify-center text-white font-bold cursor-pointer transition-all duration-200 hover:scale-110"
          style={{
            background: 'linear-gradient(135deg, #8B4513, #A0522D)',
            boxShadow: '0 3px 10px rgba(139, 69, 19, 0.4)',
          }}
          onMouseOver={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #A0522D, #8B4513)'}
          onMouseOut={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #8B4513, #A0522D)'}
        >
          ×
        </button>
        
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <span>🐾</span>
            <span>Happy Paws</span>
          </DialogTitle>
          <DialogDescription>
            {isVerifying 
              ? 'Enter the verification code sent to your email'
              : 'Join the community to rate locations and add new Paw Parks'
            }
          </DialogDescription>
        </DialogHeader>

        {isVerifying ? (
          <Card>
            <CardHeader>
              <CardTitle>Check Your Email 📧</CardTitle>
              <CardDescription>
                We've sent a verification link to your email address
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Spam Folder Reminder */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-amber-800 text-sm font-semibold mb-2">
                    📬 Don't forget to check your spam folder!
                  </p>
                  <p className="text-amber-700 text-xs leading-relaxed">
                    Sometimes our emails end up in spam. If you don't see it in your inbox within a few minutes, please check your spam/junk folder.
                  </p>
                </div>
                
                <div className="text-center py-4">
                  <div className="mb-4">
                    <svg className="w-16 h-16 mx-auto" viewBox="0 0 56 56" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="28" cy="28" r="26" fill="white" stroke="#10B981" strokeWidth="3"/>
                      <text x="28" y="38" fontSize="32" textAnchor="middle" dominantBaseline="middle">🐾</text>
                    </svg>
                  </div>
                  <p className="text-gray-700 text-sm mb-3">
                    Click the verification link in your email to activate your account.
                  </p>
                  <p className="text-gray-500 text-xs">
                    The link will automatically log you in and redirect you to your dashboard.
                  </p>
                </div>
                
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setIsVerifying(false);
                    setPendingUserId(null);
                    // Close the modal by triggering the dialog close
                    const closeButton = document.querySelector('[role="dialog"] button[aria-label="Close"]');
                    if (closeButton) closeButton.click();
                  }}
                >
                  Close Window
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="register">Register</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <Card>
                <CardHeader>
                  <CardTitle>Welcome Back</CardTitle>
                  <CardDescription>
                    Enter your credentials to access your account
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="login_username">Username</Label>
                      <Input
                        id="login_username"
                        type="text"
                        placeholder="Enter your username"
                        value={loginData.username}
                        onChange={(e) => setLoginData({
                          ...loginData,
                          username: e.target.value
                        })}
                        data-testid="login-username-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="login_password">Password</Label>
                      <div className="relative">
                        <Input
                          id="login_password"
                          type={showLoginPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          value={loginData.password}
                          onChange={(e) => setLoginData({
                            ...loginData,
                            password: e.target.value
                          })}
                          data-testid="login-password-input"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowLoginPassword(!showLoginPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          tabIndex={-1}
                        >
                          {showLoginPassword ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Forgot Password/Username Links */}
                    <div className="flex justify-between text-sm">
                      <button
                        type="button"
                        onClick={() => {
                          // Close login modal and open password reset modal
                          setIsOpen(false);
                          setShowPasswordResetModal(true);
                        }}
                        className="text-emerald-600 hover:text-emerald-700 underline"
                      >
                        Forgot Password?
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!loginData.username) {
                            toast.error('Please enter your username or email');
                            return;
                          }
                          
                          try {
                            // Send username recovery email
                            await axios.post('/auth/forgot-username', { 
                              email: loginData.username.includes('@') ? loginData.username : '' 
                            });
                            
                            // Close login modal and show spam reminder
                            setIsOpen(false);
                            setShowUsernameRecovery(true);
                          } catch (error) {
                            // Fallback: show modal anyway
                            setIsOpen(false);
                            setShowUsernameRecovery(true);
                          }
                        }}
                        className="text-emerald-600 hover:text-emerald-700 underline"
                      >
                        Forgot Username?
                      </button>
                    </div>
                    
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isLoading}
                      data-testid="login-submit-button"
                    >
                      {isLoading ? 'Logging in...' : 'Login'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="register">
              <Card>
                <CardHeader>
                  <CardTitle>Create Account</CardTitle>
                  <CardDescription>
                    Join our community to add locations and rate parks
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <Label htmlFor="register_username">Username *</Label>
                      <Input
                        id="register_username"
                        type="text"
                        placeholder="Choose a unique username"
                        value={registerData.username}
                        onChange={(e) => setRegisterData({
                          ...registerData,
                          username: e.target.value
                        })}
                        data-testid="register-username-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="register_email">Email *</Label>
                      <Input
                        id="register_email"
                        type="email"
                        placeholder="your.email@example.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({
                          ...registerData,
                          email: e.target.value
                        })}
                        data-testid="register-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="register_password">Password *</Label>
                      <div className="relative">
                        <Input
                          id="register_password"
                          type={showRegisterPassword ? "text" : "password"}
                          placeholder="At least 6 characters"
                          value={registerData.password}
                          onChange={(e) => setRegisterData({
                            ...registerData,
                            password: e.target.value
                          })}
                          data-testid="register-password-input"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          tabIndex={-1}
                        >
                          {showRegisterPassword ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="register_confirm_password">Confirm Password *</Label>
                      <div className="relative">
                        <Input
                          id="register_confirm_password"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Repeat your password"
                          value={registerData.confirmPassword}
                          onChange={(e) => setRegisterData({
                            ...registerData,
                            confirmPassword: e.target.value
                          })}
                          data-testid="register-confirm-password-input"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2 py-3 px-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <input
                        id="newsletter_subscribe"
                        type="checkbox"
                        checked={registerData.newsletter_subscribed}
                        onChange={(e) => setRegisterData({
                          ...registerData,
                          newsletter_subscribed: e.target.checked
                        })}
                        className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded cursor-pointer"
                        data-testid="newsletter-checkbox"
                      />
                      <label htmlFor="newsletter_subscribe" className="text-sm text-gray-700 cursor-pointer leading-relaxed">
                        <span className="font-medium">Join our community power!</span> Receive occasional bulk negotiated dog care and food items newsletters 🐾
                      </label>
                    </div>
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isLoading}
                      data-testid="register-submit-button"
                    >
                      {isLoading ? 'Creating Account...' : 'Create Account'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>

    {/* Password Reset Reminder */}
    <SpamFolderReminderModal
      isOpen={showPasswordReset}
      onClose={() => {
        setShowPasswordReset(false);
        setIsOpen(true);
      }}
      mode="password"
    />

    {/* Username Recovery Reminder */}
    <SpamFolderReminderModal
      isOpen={showUsernameRecovery}
      onClose={() => {
        setShowUsernameRecovery(false);
        setIsOpen(true);
      }}
      mode="username"
    />

    {/* Password Reset Modal */}
    <PasswordResetModal
      isOpen={showPasswordResetModal}
      onClose={() => {
        setShowPasswordResetModal(false);
        setIsOpen(true);
      }}
      mode="password"
    />
    </>
  );
};

export default AuthModal;